import { useMemo } from "react";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { Button } from "@themes/pollinator/ui/Button";
import { TrackingMetricsContainer } from "../TrackingMetrics";
import { TrackingCallToActionContainer } from "../TrackingCallToAction";
import { OrgInsightsContainer } from "../OrgInsights";
import { InvitationRecordsTableContainer } from "@platform/features/invitation";
import { useCreateOrganizationInvitation } from "@core/features/organization";
import { INVITATION_TYPE, type Invitation } from "@core/features/invitation";

export type Props = {
  /** All participant invitations for tracking data */
  invitations: Invitation[];
  /** Organization ID for creating new invitations */
  organizationId: number;
  /** Program ID for creating new invitations */
  programId: number | null;
  /** Template ID for creating new invitations */
  templateId: number | null;
};

/**
 * Tracking tab content for organization admin page.
 * Displays invitation links, enrollment metrics, and invitation records.
 * Supports multiple participant invitations with aggregated views.
 */
export function OrgTrackingTab({
  invitations,
  organizationId,
  programId,
  templateId,
}: Props) {
  const { mutate: createInvitation, isPending } =
    useCreateOrganizationInvitation({ organizationId });

  const handleCreateInvitation = () => {
    if (!programId || !templateId) return;
    createInvitation({
      programId,
      templateId,
      type: INVITATION_TYPE.PARTICIPANT,
    });
  };

  const invitationIds = useMemo(
    () => invitations.map((inv) => inv.id),
    [invitations]
  );

  if (invitations.length === 0) {
    return (
      <div
        className="w-full flex flex-col gap-8 pb-8 max-w-4xl"
        data-testid="org-tracking-tab"
      >
        <div>
          <Button
            onClick={handleCreateInvitation}
            disabled={isPending || !programId || !templateId}
          >
            {isPending ? "Creating..." : "Create Participant Invitation"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full flex flex-col gap-8 pb-8 max-w-4xl"
      data-testid="org-tracking-tab"
    >
      {/* Sending Institution Insights — primary surface; self-hides via
        OrgInsightsContainer when no insights have been published for the
        program yet. */}
      <OrgInsightsContainer
        programId={programId}
        organizationId={organizationId}
      />

      {/* Invitation tracking grouped at the bottom: CTA → enrollment metrics
        → records table. Less central than insights once a cycle is live. */}
      <TrackingCallToActionContainer programId={programId} organizationId={organizationId} />

      <SectionHeading
        title="Enrollment Metrics"
        description="See how your alumni and staff are joining Pollinator."
      >
        <TrackingMetricsContainer invitations={invitations} />
      </SectionHeading>

      <InvitationRecordsTableContainer invitationIds={invitationIds} />
    </div>
  );
}

export default OrgTrackingTab;
